import {
  compactGhostSamples,
  parseLapGhost,
  type GhostSample,
  type StoredLapGhost,
} from './game/lap-ghost-store.js';
import { CAR_BY_ID } from '../shared/cars.js';
import { normaliseCrewModule } from '../shared/crew-modules.js';
import {
  clearGhostChallengeRecord,
  readGhostChallengeRecord,
  writeGhostChallengeRecord,
} from './ghost-challenge-store.js';

const MAX_SHARED_SAMPLES = 96;
const MAX_LAP_TIME_MS = 15 * 60_000;
const CHALLENGE_VERSION = 3;

/** A shared ghost is meaningful only under the exact rules that recorded it. */
export const GHOST_CHALLENGE_RACE_RULES = Object.freeze({
  offlineMode: true,
  antiCheat: false,
  collisions: false,
  traffic: false,
  timeTrial: true,
  eventMode: 'time-trial',
  arcadePreset: 'standard',
} as const);

export interface GhostChallenge {
  ghost: StoredLapGhost;
  challenger: string;
}

export interface GhostChallengeCatalogState {
  challenge: GhostChallenge | null;
  selectedMapId: string;
  releasedMapId: string | null;
}

/** Reconciles a session-scoped challenge with a freshly validated map
 * catalogue. A removed map must release the challenge lock instead of silently
 * applying its car and conditions to an unrelated fallback track. */
export function reconcileGhostChallengeCatalog(
  challenge: GhostChallenge | null,
  selectedMapId: string,
  availableMapIds: readonly string[],
): GhostChallengeCatalogState {
  const selected = availableMapIds.includes(selectedMapId)
    ? selectedMapId
    : availableMapIds[0] ?? '';
  if (!challenge) return { challenge: null, selectedMapId: selected, releasedMapId: null };
  if (availableMapIds.includes(challenge.ghost.mapId)) {
    return {
      challenge,
      selectedMapId: challenge.ghost.mapId,
      releasedMapId: null,
    };
  }
  return {
    challenge: null,
    selectedMapId: selected,
    releasedMapId: challenge.ghost.mapId,
  };
}

export function encodeGhostChallenge(ghost: StoredLapGhost, challengerValue: string): string {
  const challenger = cleanText(challengerValue, 18) || 'NIGHTSHIFT DRIVER';
  const map = cleanIdentifier(ghost.mapId, 64);
  const car = cleanIdentifier(ghost.carId, 64);
  const validated = parseLapGhost(JSON.stringify(ghost), ghost.mapId);
  if (!map || !car || !CAR_BY_ID.has(car) || !validated) throw new Error('A valid ghost map, car and lap are required.');
  if (!validated.timeOfDay || !validated.weather) {
    throw new Error('The ghost does not include its exact lighting and weather setup.');
  }
  const crewModule = normaliseCrewModule(validated.crewModule);
  const samples = compactGhostSamples(validated.samples, MAX_SHARED_SAMPLES);
  if (samples.length < 20) throw new Error('The ghost needs at least 20 samples.');
  const encoder = new TextEncoder();
  const mapBytes = encoder.encode(map);
  const carBytes = encoder.encode(car);
  const crewBytes = encoder.encode(crewModule);
  const timeOfDayBytes = encoder.encode(validated.timeOfDay);
  const weatherBytes = encoder.encode(validated.weather);
  const dailyBytes = encoder.encode(validated.dailyChallengeId ?? '');
  const challengerBytes = encoder.encode(challenger);
  const headerLength = 3
    + 1 + mapBytes.length
    + 1 + carBytes.length
    + 1 + crewBytes.length
    + 1 + timeOfDayBytes.length
    + 1 + weatherBytes.length
    + 1 + dailyBytes.length
    + 1 + challengerBytes.length
    + 4 + 12 + 1;
  const bytes = new Uint8Array(headerLength + samples.length * 12);
  const view = new DataView(bytes.buffer);
  let offset = 0;
  bytes[offset++] = 0x4e;
  bytes[offset++] = 0x47;
  bytes[offset++] = CHALLENGE_VERSION;
  offset = writeBytes(bytes, offset, mapBytes);
  offset = writeBytes(bytes, offset, carBytes);
  offset = writeBytes(bytes, offset, crewBytes);
  offset = writeBytes(bytes, offset, timeOfDayBytes);
  offset = writeBytes(bytes, offset, weatherBytes);
  offset = writeBytes(bytes, offset, dailyBytes);
  offset = writeBytes(bytes, offset, challengerBytes);
  view.setUint32(offset, validated.lapTimeMs);
  offset += 4;
  for (const time of validated.sectorTimesMs ?? [0, 0, 0]) {
    view.setUint32(offset, clampInteger(time, 0, MAX_LAP_TIME_MS));
    offset += 4;
  }
  bytes[offset++] = samples.length;
  for (const sample of samples) {
    view.setUint16(offset, Math.round(clamp(sample.timeMs / validated.lapTimeMs, 0, 1) * 65_535));
    view.setUint16(offset + 2, Math.round(clamp(sample.progress, 0, 1) * 65_535));
    view.setInt16(offset + 4, clampInteger(Math.round(sample.x * 10), -32_768, 32_767));
    view.setInt16(offset + 6, clampInteger(Math.round(sample.y * 10), -32_768, 32_767));
    view.setInt16(offset + 8, clampInteger(Math.round(sample.z * 10), -32_768, 32_767));
    view.setInt16(offset + 10, clampInteger(Math.round(normaliseAngle(sample.yaw) * 10_000), -32_768, 32_767));
    offset += 12;
  }
  return base64UrlEncode(bytes);
}

export function decodeGhostChallenge(value: string): GhostChallenge | null {
  try {
    const bytes = base64UrlDecode(value);
    const version = bytes[2];
    if (
      bytes.length < 24
      || bytes[0] !== 0x4e
      || bytes[1] !== 0x47
      || (version !== 1 && version !== 2 && version !== CHALLENGE_VERSION)
    ) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const decoder = new TextDecoder();
    let offset = 3;
    const map = readText(bytes, decoder, offset);
    offset = map.offset;
    const car = readText(bytes, decoder, offset);
    offset = car.offset;
    const crew = version >= 2
      ? readText(bytes, decoder, offset)
      : { value: 'balanced', offset };
    offset = crew.offset;
    const timeOfDay = version >= 3
      ? readText(bytes, decoder, offset)
      : { value: '', offset };
    offset = timeOfDay.offset;
    const weather = version >= 3
      ? readText(bytes, decoder, offset)
      : { value: '', offset };
    offset = weather.offset;
    const dailyChallenge = version >= 3
      ? readText(bytes, decoder, offset)
      : { value: '', offset };
    offset = dailyChallenge.offset;
    const challenger = readText(bytes, decoder, offset);
    offset = challenger.offset;
    if (
      !cleanIdentifier(map.value, 64)
      || !cleanIdentifier(car.value, 64)
      || !CAR_BY_ID.has(car.value)
      || offset + 17 > bytes.length
    ) return null;
    const lapTimeMs = view.getUint32(offset);
    offset += 4;
    const sectors = [view.getUint32(offset), view.getUint32(offset + 4), view.getUint32(offset + 8)] as [number, number, number];
    offset += 12;
    if (lapTimeMs < 5_000 || lapTimeMs > MAX_LAP_TIME_MS || !validSharedSectors(sectors, lapTimeMs)) return null;
    const count = bytes[offset++] ?? 0;
    if (count < 20 || count > MAX_SHARED_SAMPLES || offset + count * 12 !== bytes.length) return null;
    const samples: GhostSample[] = [];
    for (let index = 0; index < count; index += 1) {
      samples.push({
        timeMs: Math.round(view.getUint16(offset) / 65_535 * lapTimeMs),
        progress: view.getUint16(offset + 2) / 65_535,
        x: view.getInt16(offset + 4) / 10,
        y: view.getInt16(offset + 6) / 10,
        z: view.getInt16(offset + 8) / 10,
        yaw: view.getInt16(offset + 10) / 10_000,
      });
      offset += 12;
    }
    const raw: StoredLapGhost = {
      version: 1,
      mapId: map.value,
      carId: car.value,
      crewModule: normaliseCrewModule(crew.value),
      timeOfDay: version >= 3 ? timeOfDay.value as StoredLapGhost['timeOfDay'] : undefined,
      weather: version >= 3 ? weather.value as StoredLapGhost['weather'] : undefined,
      dailyChallengeId: version >= 3 && dailyChallenge.value ? dailyChallenge.value : undefined,
      lapTimeMs,
      updatedAt: Date.now(),
      samples,
      sectorTimesMs: sectors.every((time) => time > 0) ? sectors : undefined,
      bestSectorTimesMs: sectors.every((time) => time > 0) ? sectors : undefined,
    };
    const ghost = parseLapGhost(JSON.stringify(raw), map.value);
    return ghost ? { ghost, challenger: cleanText(challenger.value, 18) || 'NIGHTSHIFT DRIVER' } : null;
  } catch {
    return null;
  }
}

export function createGhostChallengeUrl(origin: string, ghost: StoredLapGhost, challenger: string): string {
  const url = new URL('/', origin);
  url.searchParams.set('ghost', encodeGhostChallenge(ghost, challenger));
  return url.toString();
}

export function storeGhostChallenge(challenge: GhostChallenge): boolean {
  return writeGhostChallengeRecord(challenge.ghost.mapId, JSON.stringify({
    ghost: challenge.ghost,
    challenger: cleanText(challenge.challenger, 18) || 'NIGHTSHIFT DRIVER',
  }));
}

export function loadStoredGhostChallenge(mapId: string): GhostChallenge | null {
  try {
    const raw = readGhostChallengeRecord(mapId);
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    if (value && typeof value === 'object' && !Array.isArray(value) && 'ghost' in value) {
      const wrapper = value as { ghost?: unknown; challenger?: unknown };
      const ghost = parseLapGhost(JSON.stringify(wrapper.ghost), mapId);
      if (!ghost) return null;
      return {
        ghost,
        challenger: cleanText(String(wrapper.challenger ?? ''), 18) || 'CHALLENGER',
      };
    }
    const ghost = parseLapGhost(raw, mapId);
    return ghost ? { ghost, challenger: 'CHALLENGER' } : null;
  } catch {
    return null;
  }
}

export function clearStoredGhostChallenge(mapId: string): void {
  clearGhostChallengeRecord(mapId);
}

function validSharedSectors(sectors: readonly number[], lapTimeMs: number): boolean {
  if (sectors.every((time) => time === 0)) return true;
  if (!sectors.every((time) => time > 0 && time <= lapTimeMs)) return false;
  const total = sectors.reduce((sum, time) => sum + time, 0);
  return Math.abs(total - lapTimeMs) <= Math.max(250, lapTimeMs * 0.02);
}

function writeBytes(target: Uint8Array, offset: number, value: Uint8Array): number {
  if (value.length > 255) throw new Error('Ghost metadata is too long.');
  target[offset] = value.length;
  target.set(value, offset + 1);
  return offset + 1 + value.length;
}

function readText(bytes: Uint8Array, decoder: TextDecoder, offset: number): { value: string; offset: number } {
  const length = bytes[offset] ?? 0;
  const end = offset + 1 + length;
  if (end > bytes.length) throw new Error('Truncated ghost challenge.');
  return { value: decoder.decode(bytes.slice(offset + 1, end)), offset: end };
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(value: string): Uint8Array {
  if (!/^[a-zA-Z0-9_-]+$/.test(value) || value.length > 8_192) throw new Error('Invalid ghost challenge.');
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function cleanIdentifier(value: string, maximum: number): string {
  return /^[a-zA-Z0-9_-]+$/.test(value) ? value.slice(0, maximum) : '';
}

function cleanText(value: string, maximum: number): string {
  return value.replace(/[<>\u0000-\u001f]/g, '').trim().slice(0, maximum);
}

function normaliseAngle(value: number): number {
  return ((value + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, Number.isFinite(value) ? value : minimum));
}

function clampInteger(value: number, minimum: number, maximum: number): number {
  return Math.round(clamp(value, minimum, maximum));
}
